import React, { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import Swal from 'sweetalert2'
import CheckboxTree from 'react-checkbox-tree'
import 'react-checkbox-tree/lib/react-checkbox-tree.css'
import { KTSVG } from '../../_metronic/helpers'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import showLoader from '../helpers/ShowLoader'

interface UserOption {
  id: number
  value: number
  label: string
}

interface LedgerNode {
  value: string | number
  label: string
  children?: LedgerNode[]
  icon?: string | React.ReactNode
  ledger_type?: string
  ledger_code?: string
  ledger_name?: string
}

type BasicObject = {
  [key: string]: string | React.ReactNode
}

const icons: BasicObject = {
  check: <i className='fa fa-check-square text-primary' />,
  uncheck: <i className='fa fa-square-o' />,
  halfCheck: <span className='rct-icon rct-icon-half-check' />,
  expandClose: <i className='fa fa-chevron-right' />,
  expandOpen: <i className='fa fa-chevron-down' />,
  expandAll: <span className='rct-icon rct-icon-expand-all'> Expand All</span>,
  collapseAll: <span className='rct-icon rct-icon-collapse-all'> Collapse All</span>,
  parentClose: <span className='rct-icon rct-icon-parent-close' />,
  parentOpen: <span className='rct-icon rct-icon-parent-open' />,
  leaf: <i className='fa fa-file' />,
  group: <i className='fa fa-folder text-warning' />,
  gl: <i className='fa fa-file text-success' />,
}

const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined

const selectPortalStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  menu: (base: any) => ({ ...base, zIndex: 9999 }),
}

function LedgerUserAssignments() {
  const [users, setUsers] = useState<UserOption[]>([])
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [ledgers, setLedgers] = useState<LedgerNode[]>([])
  const [filteredNodes, setFilteredNodes] = useState<LedgerNode[]>([])
  const [checked, setChecked] = useState<string[]>([])
  const [expanded, setExpanded] = useState<string[]>(['Assets', 'Liabilities', 'Revenues', 'Expenses'])
  const [filterText, setFilterText] = useState('')
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [isLoadingLedgers, setIsLoadingLedgers] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const renderTreeNodes = (nodes: LedgerNode[]): LedgerNode[] => {
    return nodes.map((node) => ({
      ...node,
      icon:
        node.ledger_type && icons[node.ledger_type]
          ? icons[node.ledger_type]
          : icons.group,
      children: node.children ? renderTreeNodes(node.children) : undefined,
    }))
  }

  const fetchUsers = async () => {
    setIsLoadingUsers(true)
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}ledger-user-assignments/users`,
        getRequestOptions()
      )
      const json = await response.json()
      if (!response.ok || !json.success) {
        throw new Error(json?.message ?? 'Failed to load users.')
      }
      setUsers(Array.isArray(json.data) ? json.data : [])
    } catch (error: any) {
      Swal.fire('Error', error?.message ?? 'Unable to load users.', 'error')
    } finally {
      setIsLoadingUsers(false)
    }
  }

  const fetchUserAssignments = async (userId: number) => {
    setIsLoadingLedgers(true)
    setLoadError(null)
    showLoader()
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}ledger-user-assignments/${userId}`,
        getRequestOptions()
      )
      const json = await response.json()
      if (!response.ok || !json.success) {
        throw new Error(json?.message ?? 'Failed to load ledger assignments.')
      }

      const nodesFromApi: LedgerNode[] = Array.isArray(json?.data?.ledgers) ? json.data.ledgers : []
      const withIcons = renderTreeNodes(nodesFromApi)
      const assignedIds = (json?.data?.assignedLedgerIds ?? []).map((id: number) => String(id))

      setLedgers(withIcons)
      setFilteredNodes(withIcons)
      setChecked(assignedIds)
      setHasChanges(false)

      if (!withIcons.length) {
        setLoadError('No chart of accounts found.')
      }
    } catch (error: any) {
      setLedgers([])
      setFilteredNodes([])
      setChecked([])
      setLoadError(error?.message ?? 'Unable to load ledger assignments.')
    } finally {
      setIsLoadingLedgers(false)
      Swal.close()
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleUserChange = (option: UserOption | null) => {
    setSelectedUser(option)
    setFilterText('')
    setHasChanges(false)

    if (option?.value) {
      fetchUserAssignments(option.value)
    } else {
      setLedgers([])
      setFilteredNodes([])
      setChecked([])
      setLoadError(null)
    }
  }

  const onCheck = (nextChecked: string[]) => {
    setChecked(nextChecked.filter((value) => /^\d+$/.test(String(value))))
    setHasChanges(true)
  }

  const onExpand = (nextExpanded: string[]) => {
    setExpanded(nextExpanded)
  }

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim()
    setFilterText(value)
    filterTree(value)
  }

  const filterTree = (searchText: string) => {
    if (!searchText) {
      setFilteredNodes(ledgers)
      return
    }

    const nodesFiltered = ledgers.reduce<LedgerNode[]>(
      (filtered, node) => filterNodes(filtered, node, searchText),
      []
    )
    setFilteredNodes(nodesFiltered)
  }

  const filterNodes = (filtered: LedgerNode[], node: LedgerNode, searchText: string): LedgerNode[] => {
    const children = (node.children || []).reduce<LedgerNode[]>(
      (acc, child) => filterNodes(acc, child, searchText),
      []
    )

    if (
      node.label.toLowerCase().includes(searchText.toLowerCase()) ||
      children.length
    ) {
      filtered.push({ ...node, ...(children.length && { children }) })
    }

    return filtered
  }

  const collectGlLedgerIds = (nodes: LedgerNode[]): string[] => {
    const ids: string[] = []

    const walk = (items: LedgerNode[]) => {
      items.forEach((node) => {
        if (node.ledger_type === 'gl' && /^\d+$/.test(String(node.value))) {
          ids.push(String(node.value))
        }
        if (node.children?.length) {
          walk(node.children)
        }
      })
    }

    walk(nodes)
    return ids
  }

  const handleSelectAllGl = () => {
    const allGlIds = collectGlLedgerIds(ledgers)
    setChecked(allGlIds)
    setHasChanges(true)
  }

  const handleClearAll = () => {
    setChecked([])
    setHasChanges(true)
  }

  const selectedLedgerCount = useMemo(
    () => checked.filter((value) => /^\d+$/.test(value)).length,
    [checked]
  )

  const handleSave = async () => {
    if (!selectedUser?.value) {
      Swal.fire('Warning', 'Please select a user first.', 'warning')
      return
    }

    const ledgerIds = checked
      .filter((value) => /^\d+$/.test(value))
      .map((value) => Number(value))

    setIsSaving(true)
    showLoader()

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}ledger-user-assignments/save`,
        postRequestOptions({
          idUsers: selectedUser.value,
          ledgerIds,
        })
      )
      const json = await response.json()

      if (!response.ok || !json.success) {
        throw new Error(json?.message ?? 'Failed to save ledger assignments.')
      }

      setHasChanges(false)
      Swal.fire({
        icon: 'success',
        title: 'Saved',
        text: json.message ?? `${json.assignedCount ?? ledgerIds.length} ledger(s) assigned.`,
      })
    } catch (error: any) {
      Swal.fire('Error', error?.message ?? 'Unable to save ledger assignments.', 'error')
    } finally {
      setIsSaving(false)
      Swal.close()
    }
  }

  return (
    <div className='card card-custom'>
      <div className='card-header'>
        <div className='card-title'>
          <KTSVG
            path='/media/svg/icons/Text/Bullet-list.svg'
            className='svg-icon-1 svg-icon-primary'
          />
          <span className='text fw-bolder' style={{ marginLeft: '10px' }}>
            Ledger Assignments
          </span>
        </div>
        <div className='card-toolbar'>
          {selectedUser && (
            <button
              type='button'
              className='btn btn-sm btn-primary'
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              <i className='fa fa-save me-1' />
              Save Assignments
            </button>
          )}
        </div>
      </div>

      <div className='card-body'>
        <div className='row mb-4'>
          <label className='col-lg-2 col-form-label text-lg-end fw-semibold'>
            Select User
          </label>
          <div className='col-lg-6'>
            <Select
              isClearable
              isLoading={isLoadingUsers}
              onChange={(option) => handleUserChange(option as UserOption | null)}
              options={users}
              value={selectedUser}
              placeholder='Search and select a user...'
              menuPortalTarget={menuPortalTarget}
              menuPosition='fixed'
              styles={{
                ...selectPortalStyles,
                control: (provided) => ({
                  ...provided,
                  minHeight: '38px',
                }),
              }}
            />
          </div>
        </div>

        {!selectedUser && (
          <div className='alert alert-light-info text-center mb-0'>
            Select a user to view and assign ledgers from the chart of accounts.
          </div>
        )}

        {selectedUser && (
          <>
            <div className='row mb-3'>
              <div className='col-md-3 col-sm-6'>
                <i className='fa fa-folder text-warning me-1' /> Group
              </div>
              <div className='col-md-3 col-sm-6'>
                <i className='fa fa-file text-success me-1' /> General Ledger
              </div>
              <div className='col-md-6 text-md-end mt-2 mt-md-0'>
                <span className='badge badge-light-primary me-2'>
                  {selectedLedgerCount} ledger(s) selected
                </span>
                {hasChanges && (
                  <span className='badge badge-light-warning me-2'>Unsaved changes</span>
                )}
              </div>
            </div>

            <div className='row mb-3'>
              <div className='col-lg-8'>
                <div className='d-flex flex-wrap gap-2 align-items-center'>
                  <input
                    className='form-control form-control-sm'
                    placeholder='Search ledger by name or code...'
                    type='text'
                    value={filterText}
                    onChange={onFilterChange}
                    style={{ maxWidth: '320px' }}
                  />
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={handleSelectAllGl}
                  >
                    Select All GL
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-danger'
                    onClick={handleClearAll}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            <div className='row'>
              <div className='col-lg-8'>
                {isLoadingLedgers ? (
                  <div className='text-center py-10'>
                    <div className='spinner-border text-primary mb-3' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    <div className='fw-semibold text-muted'>Loading ledgers...</div>
                  </div>
                ) : loadError ? (
                  <div className='text-center py-10'>
                    <div className='fw-semibold text-danger'>{loadError}</div>
                    <button
                      className='btn btn-sm btn-light-primary mt-3'
                      onClick={() => selectedUser?.value && fetchUserAssignments(selectedUser.value)}
                    >
                      Retry
                    </button>
                  </div>
                ) : filteredNodes.length === 0 ? (
                  <div className='text-center py-10 text-muted fw-semibold'>
                    No ledger data available.
                  </div>
                ) : (
                  <div className='border rounded p-3 bg-light'>
                    <CheckboxTree
                      checked={checked}
                      expanded={expanded}
                      expandOnClick
                      nodes={filteredNodes as any}
                      onCheck={onCheck}
                      onExpand={onExpand}
                      icons={icons}
                      showExpandAll
                      showNodeIcon
                    />
                  </div>
                )}
              </div>

              <div className='col-lg-4'>
                <div className='card card-bordered h-100'>
                  <div className='card-header min-h-50px'>
                    <h3 className='card-title fs-6 mb-0'>How it works</h3>
                  </div>
                  <div className='card-body fs-7 text-muted'>
                    <p className='mb-2'>
                      Check the ledgers this user should access. Group folders help navigate the chart of accounts.
                    </p>
                    <p className='mb-2'>
                      Checking a group automatically selects its child ledgers. Use <strong>Select All GL</strong> to quickly assign every general ledger account.
                    </p>
                    <p className='mb-0'>
                      Click <strong>Save Assignments</strong> when you are done. Only checked ledgers will be stored for the selected user.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default LedgerUserAssignments
